import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, extractApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const initialToken = params.get("token") || "";
  const { status, loadMe, logout } = useAuth();
  const t = useTranslation();
  const navigate = useNavigate();
  const [token, setToken] = useState(initialToken);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    setToken(initialToken);
  }, [initialToken]);

  useEffect(() => {
    if (status === "authenticated" && !busy && !accepted) {
      logout();
    }
  }, [status, busy, accepted, logout]);

  const acceptWithPassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/invites/register", { token, password });
      setAccepted(true);
      await loadMe();
      toast.success(t("auth.acceptInvite.accepted"));
      navigate("/sensors", { replace: true });
    } catch (err) {
      toast.error(extractApiError(err, t("auth.acceptInvite.failed")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-background p-6 grid-background">
      <div
        className="w-full max-w-md rounded-xl bg-card border border-border p-8 space-y-5"
        data-testid="accept-invite-page"
      >
        <div>
          <div className="mono-eyebrow mb-2">{t("auth.acceptInvite.eyebrow")}</div>
          <h2 className="font-display text-2xl tracking-tight">
            {t("auth.acceptInvite.title")}
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {t("auth.acceptInvite.description")}
          </p>
        </div>
        <div className="space-y-2">
          <Label className="text-xs">{t("auth.acceptInvite.token")}</Label>
          <Input
            value={token}
            onChange={(e) => setToken(e.target.value)}
            required
            data-testid="accept-invite-token-input"
          />
        </div>

        <form onSubmit={acceptWithPassword} className="space-y-3">
          <div className="space-y-2">
            <Label className="text-xs">
              {t("common.fields.password")}{" "}
              <span className="text-muted-foreground font-mono">
                {t("common.misc.minEightChars")}
              </span>
            </Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              data-testid="accept-invite-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={busy || !token || !password || status === "authenticated"}
            className="w-full rounded-lg"
            data-testid="accept-invite-new-user-button"
          >
            {busy ? t("auth.acceptInvite.submitting") : t("auth.acceptInvite.submit")}
          </Button>
          <div className="text-xs text-muted-foreground text-center">
            {t("auth.acceptInvite.helper")}{" "}
            <Link
              to="/login"
              className="text-foreground hover:underline"
              data-testid="accept-invite-login-link"
            >
              {t("auth.acceptInvite.regularSignIn")}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
